"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UnlockLifetimeButton } from "@/components/UnlockLifetimeButton";
import { Check, Loader2, Crown, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import config from "@/config";
import { ALL_PLAN_FEATURES, FEATURES_SCOPE_LABEL } from "@/lib/features";
import { promotions } from "@/config/promotions";

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showMaybeLater?: boolean;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  secondaryActionDisabled?: boolean;
}

const PLAN_FEATURES = ALL_PLAN_FEATURES.map((label) => ({
  label,
  included: true,
}));

export function UpgradeDialog({
  open,
  onOpenChange,
  showMaybeLater = false,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionDisabled = false,
}: UpgradeDialogProps) {
  const included = PLAN_FEATURES.filter((feature) => feature.included);
  const [selectedPlan, setSelectedPlan] = useState<"lifetime" | "monthly">(
    "lifetime"
  );
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(false);
  const showBlackFridayPromo = promotions.blackFridayEnabled;

  const handleMonthlyCheckout = async () => {
    if (isMonthlyLoading) return;
    try {
      setIsMonthlyLoading(true);
      const response = await fetch("/api/polar/checkout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: "monthly" }),
      });

      if (response.status === 401) {
        const callbackUrl = encodeURIComponent("/checkout?plan=monthly");
        window.location.href = `/signin?callbackUrl=${callbackUrl}`;
        return;
      }

      if (response.status === 409) {
        const payload = (await response.json().catch(() => ({}))) as {
          code?: string;
        };
        if (payload.code === "subscription_active") {
          window.location.href = `${config.routes.analysis.path}?checkout=checkout-subscription-active`;
          return;
        }
      }

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          payload.error || "Unable to start checkout. Please try again."
        );
      }

      const payload = (await response.json().catch(() => ({}))) as {
        url?: string;
      };

      if (!payload.url) {
        throw new Error("Checkout URL missing in response.");
      }

      window.location.href = payload.url;
    } catch (error) {
      console.error("Monthly checkout failed", error);
      setIsMonthlyLoading(false);
    }
  };

  // minimal pricing handled inside the plan cards

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">
            Unlimited access upgrade
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose a plan to unlock all monitoring features.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 sm:space-y-6">
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold tracking-tight text-foreground text-center">
              Unlock all features
            </h3>

            <div
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
              role="radiogroup"
              aria-label="Choose your plan"
            >
              {/* Monthly on the left */}
              <button
                type="button"
                role="radio"
                aria-checked={selectedPlan === "monthly"}
                onClick={() => setSelectedPlan("monthly")}
                className={
                  `relative w-full rounded-xl border p-4 text-left transition focus:outline-none focus-visible:ring-2 ` +
                  (selectedPlan === "monthly"
                    ? "border-[#10b981] bg-[#10b981]/5 ring-2 ring-[#10b981]/60"
                    : "border-border hover:bg-muted/50")
                }
                data-slot="plan-card-monthly"
              >
                {selectedPlan === "monthly" && (
                  <span className="absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#10b981]">
                    <Check
                      className="h-3.5 w-3.5 text-white"
                      strokeWidth={2.5}
                    />
                  </span>
                )}
                <div className="text-foreground">
                  <div className="text-lg font-semibold flex items-center gap-2">
                    <Zap className="h-4 w-4 text-[#10b981]" />
                    <span>Monthly</span>
                  </div>
                  <div className="mt-1 flex items-end gap-2">
                    <span className="text-3xl font-semibold">$5</span>
                    <span className="text-xs text-muted-foreground">
                      per month
                    </span>
                  </div>
                </div>
              </button>

              {/* Lifetime on the right (default selected) */}
              <button
                type="button"
                role="radio"
                aria-checked={selectedPlan === "lifetime"}
                onClick={() => setSelectedPlan("lifetime")}
                className={
                  `relative w-full rounded-xl border p-4 text-left transition focus:outline-none focus-visible:ring-2 ` +
                  (selectedPlan === "lifetime"
                    ? "border-[#10b981] bg-[#10b981]/5 ring-2 ring-[#10b981]/60"
                    : "border-border hover:bg-muted/50")
                }
                data-slot="plan-card-lifetime"
              >
                {selectedPlan === "lifetime" && (
                  <span className="absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#10b981]">
                    <Check
                      className="h-3.5 w-3.5 text-white"
                      strokeWidth={2.5}
                    />
                  </span>
                )}
                <div className="text-foreground">
                  <div className="text-lg font-semibold flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-500 fill-amber-500" />
                    <span>Lifetime</span>
                  </div>
                  <div className="mt-1 flex flex-col gap-1">
                    {showBlackFridayPromo && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-amber-500 bg-amber-500/10 text-[10px] font-medium text-amber-600 dark:text-amber-500 uppercase tracking-wider w-fit">
                        Black Friday Deal
                      </span>
                    )}
                    <div className="flex items-end gap-2">
                      {showBlackFridayPromo && (
                        <span className="text-lg text-muted-foreground line-through">
                          $35
                        </span>
                      )}
                      <span className="text-3xl font-semibold">
                        {showBlackFridayPromo ? "$25" : "$35"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        one-time
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {FEATURES_SCOPE_LABEL}
              </p>
              <ul className="space-y-1.5 text-xs sm:text-sm text-muted-foreground">
                {included.map((feature) => (
                  <li key={feature.label} className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-[#10b981] text-[#10b981]">
                      <Check className="h-2 w-2" strokeWidth={2.5} />
                    </span>
                    <span className="leading-relaxed text-foreground">
                      {feature.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-2">
            {selectedPlan === "lifetime" ? (
              <UnlockLifetimeButton
                size="lg"
                className="w-full rounded-xl text-sm font-medium"
              >
                Get Lifetime Access
              </UnlockLifetimeButton>
            ) : (
              <Button
                size="lg"
                className="w-full rounded-xl text-sm font-medium"
                onClick={handleMonthlyCheckout}
                disabled={isMonthlyLoading}
              >
                {isMonthlyLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting…
                  </>
                ) : (
                  "Subscribe Monthly"
                )}
              </Button>
            )}

            <p className="text-xs text-muted-foreground text-center">
              {selectedPlan === "lifetime"
                ? "No subscription • One-time payment"
                : "Cancel anytime"}
            </p>

            {secondaryActionLabel && onSecondaryAction && (
              <>
                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border/60" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-3 text-muted-foreground">
                      or
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full rounded-xl text-sm font-medium"
                  onClick={onSecondaryAction}
                  disabled={secondaryActionDisabled}
                >
                  {secondaryActionLabel}
                </Button>
              </>
            )}
            {showMaybeLater && (
              <Button
                variant="outline"
                size="lg"
                className="w-full rounded-xl text-sm font-medium"
                onClick={() => onOpenChange(false)}
              >
                Maybe Later
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
